/**
 * Colour-swatch grouping by `style:xxxxx` product tag.
 *
 * Colourways of one style are separate products sharing a `style:xxxxx` tag.
 * This module fetches every product carrying a given tag through the Section
 * Rendering API (sections/api-related-swatches.liquid), deduping concurrent
 * requests and caching the response per tag for the session — a collection page
 * full of cards from the same style makes one request, not one per card.
 *
 * Two consumers:
 *   - window.jcSwatchGroup.load()/parse() — used by the PDP swatch row, which
 *     moves the returned nodes straight into #color-swatches-container.
 *   - the product-card row below, which upgrades the server-rendered curated
 *     swatches to the full tagged group so the "+N more colours" count is right.
 *
 * Everything degrades to the server-rendered curated swatches: no tag, a failed
 * request or an empty result all leave the existing markup untouched.
 */
(function () {
  var CACHE_PREFIX = 'swatch:';
  var CARD_MAX_DOTS = 3;
  var inFlight = new Map();

  function sectionUrl(styleTag) {
    var searchUrl = (window.theme && theme.routes && theme.routes.search_url) || '/search';
    return (
      searchUrl +
      '?q=' + encodeURIComponent('tag:"' + styleTag + '"') +
      '&type=product&section_id=api-related-swatches'
    );
  }

  function readCache(key) {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
  }

  function writeCache(key, value) {
    try { sessionStorage.setItem(key, value); } catch (e) { /* private mode / quota */ }
  }

  /** Resolves with the section HTML for a style tag. */
  function load(styleTag) {
    if (!styleTag) return Promise.reject(new Error('jcSwatchGroup: no style tag'));

    var key = CACHE_PREFIX + styleTag;
    var cached = readCache(key);
    if (cached) return Promise.resolve(cached);

    if (inFlight.has(styleTag)) return inFlight.get(styleTag);

    var request = fetch(sectionUrl(styleTag))
      .then(function (response) {
        return response.ok ? response.text() : Promise.reject(new Error(response.status));
      })
      .then(function (html) {
        writeCache(key, html);
        inFlight.delete(styleTag);
        return html;
      })
      .catch(function (error) {
        inFlight.delete(styleTag);
        throw error;
      });

    inFlight.set(styleTag, request);
    return request;
  }

  /**
   * Parses section HTML into swatch descriptors, deduped by product handle.
   * `el` is a fresh node each call, so callers can move it into their own DOM.
   */
  function parse(html) {
    var template = document.createElement('template');
    template.innerHTML = (html || '').trim();

    var results = template.content.querySelector('.related-swatches-results');
    if (!results) return [];

    var seen = new Set();
    return Array.prototype.slice
      .call(results.querySelectorAll('.color-swatch'))
      .map(function (anchor) {
        return {
          handle: (anchor.dataset.handle || '').toLowerCase(),
          hex: anchor.dataset.hex || '',
          label: anchor.getAttribute('data-color') || '',
          url: anchor.getAttribute('href') || '#',
          title: anchor.dataset.title || '',
          image: anchor.dataset.image || '',
          imageSecond: anchor.dataset.imageSecond || '',
          price: anchor.dataset.price || '',
          comparePrice: anchor.dataset.comparePrice || '',
          // Left undefined for swatch HTML cached before the attribute existed,
          // so the card keeps its server-rendered label rather than losing it.
          saleLabel: anchor.dataset.saleLabel,
          variants: anchor.dataset.variants || '',
          el: anchor
        };
      })
      .filter(function (swatch) {
        if (!swatch.handle || seen.has(swatch.handle)) return false;
        seen.add(swatch.handle);
        return true;
      });
  }

  function invalidate(styleTag) {
    try { sessionStorage.removeItem(CACHE_PREFIX + styleTag); } catch (e) {}
  }

  window.jcSwatchGroup = { load: load, parse: parse, invalidate: invalidate };

  /* ------------------------------------------------------------------
     Product cards
     ------------------------------------------------------------------ */

  function swatchDot(swatch, isCurrent) {
    var name = swatch.label || 'Colour';
    // A button, not a span: choosing a colourway is an action, so it needs to
    // be reachable and operable from the keyboard like any other control.
    var dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'color-swatch' + (isCurrent ? ' color-swatch-active' : '');
    dot.setAttribute('data-tooltip', name);
    dot.setAttribute('aria-label', name);
    dot.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');

    dot.dataset.handle = swatch.handle || '';
    dot.dataset.url = swatch.url || '';
    dot.dataset.title = swatch.title || '';
    dot.dataset.image = swatch.image || '';
    dot.dataset.imageSecond = swatch.imageSecond || '';
    dot.dataset.price = swatch.price || '';
    dot.dataset.comparePrice = swatch.comparePrice || '';
    if (swatch.saleLabel !== undefined) dot.dataset.saleLabel = swatch.saleLabel;
    dot.dataset.variants = swatch.variants || '';

    var fill = document.createElement('span');
    fill.style.backgroundColor = swatch.hex;
    dot.appendChild(fill);

    return dot;
  }

  function moreIndicator(count) {
    var text = count === 1 ? '1 more colour' : count + ' more colours';
    var more = document.createElement('span');
    more.className = 'color-more';
    more.setAttribute('data-tooltip', text);
    more.setAttribute('aria-label', text);
    more.textContent = '+';
    return more;
  }

  function renderCard(container, swatches) {
    var currentHandle = (container.dataset.currentHandle || '').toLowerCase();
    var list = swatches.slice();

    // The card's own product may be missing from the search — untagged, sold out
    // and filtered, or past the result limit — so re-add it from the markup.
    var hasCurrent = list.some(function (swatch) { return swatch.handle === currentHandle; });
    if (!hasCurrent) {
      var hex = container.dataset.currentHex || '';
      var label = container.dataset.currentName || '';
      if (hex || label) list.push({ handle: currentHandle, hex: hex, label: label });
    }
    if (!list.length) return;

    // The card's own colour leads (the CSS marks the first dot), rest alphabetical.
    var own = list.filter(function (swatch) { return swatch.handle === currentHandle; });
    var rest = list
      .filter(function (swatch) { return swatch.handle !== currentHandle; })
      .sort(function (a, b) { return a.label.localeCompare(b.label); });
    var ordered = own.concat(rest);

    // Only colours with a hex can be drawn, but every colourway counts towards
    // the total — so a product missing its swatch metafield still shows up in "+N".
    var dots = ordered.filter(function (swatch) { return swatch.hex; }).slice(0, CARD_MAX_DOTS);
    var remaining = ordered.length - dots.length;

    var fragment = document.createDocumentFragment();
    dots.forEach(function (swatch) {
      fragment.appendChild(swatchDot(swatch, swatch.handle === currentHandle));
    });
    if (remaining > 0) fragment.appendChild(moreIndicator(remaining));

    container.replaceChildren(fragment);
  }

  /* ------------------------------------------------------------------
     Choosing a colourway on a card
     ------------------------------------------------------------------ */

  /**
   * Repaints a card as the chosen colourway: images, title, price and every
   * link. It does not navigate — the card becomes that product, and the usual
   * click targets take you there.
   */
  function applySwatch(card, dot) {
    var url = dot.dataset.url;
    if (!url || url === '#') return;

    var first = card.querySelector('.product-image-first');
    var second = card.querySelector('.product-image-second');
    var title = card.querySelector('.jc-product-card__title');
    var imageLink = card.querySelector('.jc-product-card__image-link');
    var prices = card.querySelector('.product__prices');

    if (first && dot.dataset.image) {
      first.src = dot.dataset.image;
      // The srcset still describes the old colourway and would win over src.
      first.removeAttribute('srcset');
      if (dot.dataset.title) first.alt = dot.dataset.title;
    }

    if (second) {
      if (dot.dataset.imageSecond) {
        second.src = dot.dataset.imageSecond;
        second.removeAttribute('srcset');
        second.hidden = false;
      } else {
        // No second shot for this colour: hide it so the hover doesn't reveal
        // the previous colourway's image.
        second.hidden = true;
      }
    }

    if (title) {
      if (dot.dataset.title) title.textContent = dot.dataset.title;
      title.href = url;
    }
    if (imageLink) {
      imageLink.href = url;
      if (dot.dataset.title) imageLink.setAttribute('aria-label', dot.dataset.title);
    }

    if (prices && dot.dataset.price) {
      var compare = dot.dataset.comparePrice;
      prices.textContent = '';
      if (compare) {
        var was = document.createElement('s');
        was.textContent = compare;
        prices.appendChild(was);
      }
      var now = document.createElement('span');
      now.className = (compare ? 'product__price--on-sale' : 'product__price') + ' js-final-price';
      now.textContent = dot.dataset.price;
      prices.appendChild(now);
    }

    applySaleLabel(card, dot);

    card.querySelectorAll('.jc-product-card__colors .color-swatch').forEach(function (el) {
      var isChosen = el === dot;
      el.classList.toggle('color-swatch-active', isChosen);
      if (el.tagName === 'BUTTON') el.setAttribute('aria-pressed', isChosen ? 'true' : 'false');
    });

    rebuildQuickAdd(card, dot);
  }

  /**
   * Swaps the card's sale / discount badge ("Sale", "Save 30%") for the chosen
   * colourway's, adding or removing it as that colourway goes on or off sale.
   * It goes first in the labels so it wins over `cardlabel_` tag labels, as
   * the server-rendered card does.
   */
  function applySaleLabel(card, dot) {
    if (dot.dataset.saleLabel === undefined) return;

    var labels = card.querySelector('.jc-product-card__labels');
    if (!labels) return;

    var label = labels.querySelector('[data-sale-label]');
    var text = dot.dataset.saleLabel;

    if (!text) {
      if (label) label.remove();
      return;
    }

    if (!label) {
      label = document.createElement('div');
      label.className = 'jc-product-card__label jc-product-card__label--sale';
      label.setAttribute('data-sale-label', '');
      labels.insertBefore(label, labels.firstChild);
    }
    label.textContent = text;
  }

  /** Strips the group prefix so "Petite (XS)" shows as "XS", matching the card. */
  function sizeLabel(name) {
    return name
      .replace(/^Petite \(/, '')
      .replace(/^Tall \(/, '')
      .replace(/\)$/, '')
      .replace(/-/g, '/');
  }

  /**
   * Repoints the quick-add sizes at the chosen colourway. The buttons keep the
   * classes and attributes the delegated add-to-cart and tab handlers look for,
   * so nothing needs rebinding.
   */
  function rebuildQuickAdd(card, dot) {
    var quickAdd = card.querySelector('.jc-quick-add');
    if (!quickAdd) return;

    var variants;
    try {
      variants = JSON.parse(dot.dataset.variants || '[]');
    } catch (e) {
      variants = [];
    }

    // Without sizes to offer, hiding beats leaving the previous colourway's
    // buttons in place — those carry variant ids for the wrong product.
    if (!variants.length) {
      quickAdd.hidden = true;
      return;
    }
    quickAdd.hidden = false;

    // A map, not a joined string: comparing option names by substring is what
    // used to drop XS (inside XXS) and S (inside both).
    var seen = Object.create(null);
    var groups = { regular: [], petite: [], tall: [] };

    variants.forEach(function (v) {
      var name = v.t == null ? '' : String(v.t);
      if (!name || seen[name]) return;
      seen[name] = true;

      if (name.indexOf('Petite') !== -1) groups.petite.push(v);
      else if (name.indexOf('Tall') !== -1) groups.tall.push(v);
      else groups.regular.push(v);
    });

    var frag = document.createDocumentFragment();
    var hasGroups = groups.petite.length > 0 || groups.tall.length > 0;

    if (hasGroups) {
      var tabs = document.createElement('div');
      tabs.className = 'jc-quick-add__tabs';
      [['regular', 'Regular'], ['petite', 'Petite'], ['tall', 'Tall']].forEach(function (pair) {
        if (!groups[pair[0]].length) return;
        var tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'size-tab' + (pair[0] === 'regular' ? ' active' : '');
        tab.dataset.group = pair[0];
        tab.textContent = pair[1];
        tabs.appendChild(tab);
      });
      frag.appendChild(tabs);
    }

    ['regular', 'petite', 'tall'].forEach(function (name) {
      if (!groups[name].length) return;
      var group = document.createElement('div');
      group.className = 'jc-quick-add__group';
      group.dataset.group = name;
      if (name !== 'regular') group.style.display = 'none';

      groups[name].forEach(function (v) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'jc-quick-add__size' + (v.a ? '' : ' sold-out');
        button.setAttribute('quick-add-qf', '');
        button.dataset.id = v.id;
        button.dataset.quantity = '1';
        button.textContent = sizeLabel(String(v.t));
        group.appendChild(button);
      });

      frag.appendChild(group);
    });

    quickAdd.replaceChildren(frag);
  }

  // Delegated: cards are rendered continuously by search, load-more and
  // recommendations, so binding per card would miss everything added later.
  document.addEventListener('click', function (event) {
    var dot = event.target.closest('.jc-product-card__colors .color-swatch');
    // Keyed off the data rather than the tag: the server renders placeholder
    // swatches with no colourway attached, and those stay inert until the
    // style group loads and replaces them.
    if (!dot || !dot.dataset.url) return;
    var card = dot.closest('.jc-product-card');
    if (!card) return;
    event.preventDefault();
    applySwatch(card, dot);
  });

  function enhanceCard(container) {
    if (container.dataset.swatchGroupApplied) return;
    container.dataset.swatchGroupApplied = 'true';

    load(container.dataset.styleTag)
      .then(function (html) { renderCard(container, parse(html)); })
      .catch(function () { /* leave the curated swatches in place */ });
  }

  var observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              observer.unobserve(entry.target);
              enhanceCard(entry.target);
            });
          },
          { rootMargin: '200px' }
        )
      : null;

  function scan(root) {
    var selector = '.jc-product-card__colors[data-style-tag]:not([data-swatch-group-applied])';
    (root || document).querySelectorAll(selector).forEach(function (container) {
      if (observer) {
        observer.observe(container);
      } else {
        enhanceCard(container);
      }
    });
  }

  function init() {
    scan(document);

    // Cards arrive later from filtering, load-more and infinite scroll, none of
    // which emit an event to hook — watch the DOM instead.
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = requestAnimationFrame(function () {
        pending = null;
        scan(document);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
